/**
 * Deposit Page - Cash-in flow (location first, then calculator)
 *
 * When no location is selected: shows Locator. When location is selected: shows
 * calculator (amount, exchange amount, fees, "Pay at location" total). FeeQuote
 * runs after amount is entered. Continue goes to phone (or back to review if editing).
 */

import { t } from "@lingui/core/macro";
import * as Sentry from "@sentry/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { TokenUSDC } from "@web3icons/react";
import { ChevronDown, Info, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { CircleFlag } from "react-circle-flags";
import { RampsButton } from "@/components/ramps/Button";
import { EditableTextWithThickCaret as EditableText } from "@/components/ramps/EditableTextWithThickCaret";
import { RampsSelectBottomSheet } from "@/components/ramps/RampsSelectBottomSheet";
import {
	CORE_API_ERROR_CODES,
	type CoreApiErrorCode,
} from "@/constants/core-api-error-codes";
import { useApiClientWithAutoVersion } from "@/hooks/useApiClientWithAutoVersion";
import { useCalculatorExperiment } from "@/hooks/useCalculatorExperiment";
import { useContinue } from "@/hooks/useContinue";
import { useCountries } from "@/hooks/useCountries";
import { FEATURE_FLAGS, useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useAnalytics } from "@/hooks/usePostHog";
import { toast } from "@/lib/toast";
import { capitalizeFirstLetter, formatNumber } from "@/lib/utils";
import { getActiveUserLanguageTag } from "@/utils/user-language";
import type { Country, QuoteRequest, QuoteResponse } from "../api";
import { CoreApiErrorPage } from "../components/ramps/CoreApiErrorPage";
import { DepositCalculator } from "../components/ramps/DepositCalculator";
import { LegalNameConfirmationDrawer } from "../components/ramps/LegalNameConfirmationDrawer";
import { Locator } from "../components/ramps/Locator";
import { MaintenancePage } from "../components/ramps/MaintenancePage";
import { RampsText } from "../components/ramps/Text";
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerScrollBody,
	DrawerTitle,
} from "../components/ui/drawer";
import { Progress } from "../components/ui/progress";
import { Skeleton } from "../components/ui/skeleton";
import { getCountriesWithSubdivisions } from "../constants/subdivisions";
import {
	formatAmount,
	isAmountReady,
	minMaxError,
	validateAmount,
} from "../domain/calculator";
import { navigateWithRampsState } from "../lib/ramps-navigation";
import {
	getApiVersionFromSearchParams,
	getAuthTokenFromSearchParams,
	validateRampsSearch,
} from "../lib/ramps-search-schema";
import { rampsActions, useRampsStore } from "../stores/use-ramps-store";
import { ANALYTICS_EVENTS } from "../utils/analytics";
import {
	buildDepositQuoteRequest as buildDepositQuoteRequestFromParams,
	buildDestinationCountrySubdivisionCode,
} from "../utils/buildDepositQuoteRequest";
import { resolveCashInTaxes } from "../utils/cash-in-taxes";
import { convertISO2ToISO3 } from "../utils/country-utils";
import { resolveDepositSendCurrency } from "../utils/deposit-send-currency";

// Types for UI components
interface CountryOption {
	value: string;
	label: string;
	countryCode: string;
}

function DepositPage() {
	const search = Route.useSearch();
	const navigate = useNavigate();
	const store = useRampsStore();
	const continueToNextPage = useContinue();
	const { trackPageView, trackEvent } = useAnalytics();
	const { isFeatureEnabled } = useFeatureFlags();

	// Location selector bottom sheet (when calculator is visible)
	const [locationSheetOpen, setLocationSheetOpen] = useState(false);
	// Legal disclaimer bottom sheet for "Pay at location" info
	const [disclaimerSheetOpen, setDisclaimerSheetOpen] = useState(false);
	// RAMPS-4410: legal-name verification bottom sheet shown to brand-new users
	// on Continue, before the flow proceeds toward sendValidate.
	const [showNameConfirmDrawer, setShowNameConfirmDrawer] = useState(false);

	// A/B Experiment: Calculator UI test (reuse same experiment as cash-out)
	const { variant: experimentVariant, getExperimentProperties } =
		useCalculatorExperiment();

	// Get min/max amounts from store (set by PostHog feature flags)
	// Default minimum amount is 15
	const minAmount = useRampsStore((state) => state.minAmount || 15);
	const maxAmount = useRampsStore((state) => state.maxAmount || 10000);
	const maxSendLimit = useRampsStore((state) => state.maxSendLimit || 950);

	// Centralized amount management
	const [readyForQuote, setReadyForQuote] = useState(false);
	const [inputAmount, setInputAmount] = useState("");
	const [isAmountEditable, setIsAmountEditable] = useState(true);
	const [isPendingNewQuote, setIsPendingNewQuote] = useState(false);
	const readyForQuoteTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const hasInitializedFromStore = useRef(false);
	const prevLocationIdRef = useRef<string | undefined>(undefined);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (readyForQuoteTimeoutRef.current) {
				clearTimeout(readyForQuoteTimeoutRef.current);
			}
		};
	}, []);

	// Detect API version from URL parameters
	const authToken = getAuthTokenFromSearchParams(search) || store.token;
	const detectedVersionFromUrl = getApiVersionFromSearchParams(search);
	// Use detected version if explicit, otherwise fall back to store or default
	const detectedVersion = detectedVersionFromUrl ?? store.apiVersion ?? "v3";

	// Set API version synchronously
	if (authToken && store.apiVersion !== detectedVersion) {
		rampsActions.setApiVersion(detectedVersion);
	}

	// Initialize API client
	const { apiClient } = useApiClientWithAutoVersion({
		authToken,
		walletAddress: search.walletAddress,
	});

	// Quote mutation for when returning from review with edited amount
	// This ensures we get a new transactionId and updated fees before going back to review
	const quoteMutation = useMutation({
		mutationFn: async (quoteRequest: QuoteRequest) => {
			return apiClient.quote.getQuote(quoteRequest, {
				params: { operation: "deposit" },
			});
		},
		onSuccess: (quoteResponse) => {
			// Store the quote response (includes new transactionId)
			rampsActions.setQuoteResponse(quoteResponse);

			// Set service option for deposit flow
			rampsActions.setServiceOption("DIRECT_TO_ACCT");

			// If sender profile is available, prefill user data
			if (quoteResponse.senderProfile) {
				rampsActions.setSenderProfile(quoteResponse.senderProfile);
			}

			trackEvent("deposit_amount_edited_quote_success", {
				amount: inputAmount,
				country: store.payoutCountry,
				from_review: true,
			} as Record<string, unknown>);

			// Navigate to review page
			const searchWithoutFromReview = { ...search };
			delete searchWithoutFromReview.from_review;
			navigate({
				to: "/ramps/review",
				search: { ...searchWithoutFromReview, step: "review" },
			});
		},
		onError: () => {
			toast.error(t`Failed to update quote. Please try again.`);
		},
	});

	// Derive UI state from experiment variant
	const isNewCalculatorEnabled = experimentVariant === "test";

	// Track experiment-specific event once when variant is loaded
	const hasTrackedExperimentEvent = useRef(false);

	// Sync URL search params with store on route entry
	useEffect(() => {
		if (search.transaction_id) {
			rampsActions.setTransactionId(search.transaction_id);
		}
		const tokenFromUrl = search.accesstoken || search.token;
		if (tokenFromUrl) {
			rampsActions.setToken(tokenFromUrl);
		}
		if (search.walletAddress) {
			rampsActions.setWalletAddress(search.walletAddress);
		}
		// Handle amount initialization
		// v3: Use prepareAmount if present (ignore URL param)
		// v2/v5: Use URL param
		if (store.apiVersion === "v3" && store.prepareAmount) {
			const formattedAmount = formatAmount(store.prepareAmount);
			setInputAmount(formattedAmount);
			setIsAmountEditable(false);
			hasInitializedFromStore.current = true;
		} else if (search.amount && store.apiVersion !== "v3") {
			// v2/v5: Use URL param (only if not v3)
			const formattedAmount = formatAmount(search.amount);
			setInputAmount(formattedAmount);
			setIsAmountEditable(false);
			hasInitializedFromStore.current = true;
		} else if (
			search.from_review === "true" &&
			store.amount &&
			!hasInitializedFromStore.current
		) {
			// Initialize amount from store when coming from review (only once)
			// Use a function form of setState to avoid dependency on current inputAmount value
			// This prevents overwriting user input while typing
			setInputAmount((current) => {
				// Only set if current value is empty (initial state)
				if (!current || current === "") {
					hasInitializedFromStore.current = true;
					return formatAmount(store.amount);
				}
				// User has already started typing, don't overwrite
				return current;
			});
		} else if (
			store.cashInLocation &&
			store.amount &&
			Number(store.amount) >= 1 &&
			!hasInitializedFromStore.current
		) {
			// Initialize from store when returning to deposit (e.g. back from phone page)
			// Restores amount so user can edit and get a fresh quote
			hasInitializedFromStore.current = true;
			setInputAmount((current) =>
				!current || current === "" ? formatAmount(store.amount) : current,
			);
			setReadyForQuote(true);
		}

		// Track page view
		trackPageView("deposit", {
			page_name: "deposit",
		});
	}, [
		search.transaction_id,
		search.token,
		search.accesstoken,
		search.walletAddress,
		search.amount,
		search.from_review,
		store.amount,
		store.prepareAmount,
		store.apiVersion,
		store.cashInLocation,
		trackPageView,
	]);

	// Track experiment-specific event
	useEffect(() => {
		if (hasTrackedExperimentEvent.current || experimentVariant === null) {
			return;
		}
		hasTrackedExperimentEvent.current = true;
		trackEvent(ANALYTICS_EVENTS.CALCULATOR_PAGE_VIEWED, {
			"$feature/ramps-new-calculator-page-enabled": experimentVariant,
			experiment_variant: experimentVariant,
			experiment_name: "calculator-ui-ab-test",
			flow_type: "deposit",
		} as Parameters<typeof trackEvent>[1]);
	}, [experimentVariant, trackEvent]);

	// Fetch service options for deposit
	const serviceOptionsQuery = useQuery({
		queryKey: [
			"serviceOptions",
			"deposit",
			store.assetCode,
			store.payoutCountry,
		],
		queryFn: async () => {
			const response = await apiClient.serviceOptions.getServiceOptions({
				operation: "deposit",
				assetCode: store.assetCode,
				userSelectedCountry: store.payoutCountry,
			});

			// Set country if API provides one, we don't have a selection, and the
			// country is actually supported for cash-in (RAMPS-4406)
			if (
				response.withdrawCountry &&
				!store.payoutCountry &&
				(response.cashInCountries || []).includes(response.withdrawCountry)
			) {
				rampsActions.setPayoutCountry(response.withdrawCountry);
			}

			// Store user coordinates from serviceOptions response (fallback for when Geolocation API is denied)
			rampsActions.setUserCoordinates(
				response.latitude || "",
				response.longitude || "",
			);

			return response;
		},
		enabled: !!authToken && store.isPrepareComplete, // Wait for prepare/context before calling
		staleTime: 2 * 60 * 1000,
	});

	const isCashInMaintenance =
		isFeatureEnabled(FEATURE_FLAGS.RAMPS_CASHIN_MAINTENANCE) ||
		serviceOptionsQuery.data?.isServerDown === true;

	// Use countries hook for centralized data access
	const {
		filteredCountries,
		isLoading: countriesLoading,
		isError: isCountriesError,
	} = useCountries({
		allowedCountryCodes: serviceOptionsQuery.data?.cashInCountries || [],
	});

	// RAMPS-4405: any core API failure (after retries) leaves the flow
	// unrecoverable — collect support codes and swap in the error splash below.
	const coreApiErrorCodes = useMemo(() => {
		const codes: CoreApiErrorCode[] = [];
		if (store.isPrepareFailed) codes.push(CORE_API_ERROR_CODES.prepare);
		if (isCountriesError) codes.push(CORE_API_ERROR_CODES.countries);
		if (serviceOptionsQuery.isError)
			codes.push(CORE_API_ERROR_CODES.serviceOptions);
		return codes;
	}, [store.isPrepareFailed, isCountriesError, serviceOptionsQuery.isError]);

	// Country options
	const allCountries = filteredCountries || [];
	const allowedIso3 = useMemo(() => {
		const list = serviceOptionsQuery.data?.cashInCountries || [];
		return list.length ? list : allCountries.map((c) => c.iso3Code);
	}, [serviceOptionsQuery.data?.cashInCountries, allCountries]);

	const filteredCountriesForOptions = allCountries.filter((c) =>
		allowedIso3.includes(c.iso3Code),
	);

	// One-time country resolution
	useEffect(() => {
		const hasServiceOptions =
			!!serviceOptionsQuery.data && !serviceOptionsQuery.isLoading;
		const hasCountries = allCountries.length > 0 && !countriesLoading;
		const isReady = hasServiceOptions && hasCountries;

		if (!isReady || store.payoutCountry) {
			return;
		}

		// Default to USA if available
		const defaultCountry = allowedIso3.includes("USA") ? "USA" : allowedIso3[0];
		if (defaultCountry) {
			rampsActions.setPayoutCountry(defaultCountry);
		}
	}, [
		serviceOptionsQuery.data,
		serviceOptionsQuery.isLoading,
		allCountries,
		allowedIso3,
		countriesLoading,
		store.payoutCountry,
	]);

	// Get effective country for display
	const selectedCountryIso3 = store.payoutCountry || "USA";

	// Country options for the <Select/>
	const countryOptions: CountryOption[] = filteredCountriesForOptions.map(
		(country: Country) => ({
			value: country.iso3Code,
			label: country.name,
			countryCode: country.iso2Code.toLowerCase(),
		}),
	);

	const selectedCountry = countryOptions.find(
		(c) => c.value === selectedCountryIso3,
	);

	// Deposit currency (sendCurrencyCode): the serviceOptions DIRECT_TO_ACCT item's
	// sendCurrencyCodes[0] is the source of truth, not the countries endpoint (RAMPS-4509).
	const depositSendCurrency = useMemo(
		() => resolveDepositSendCurrency(serviceOptionsQuery.data?.serviceOptions),
		[serviceOptionsQuery.data?.serviceOptions],
	);
	// Display-only fallback while serviceOptions load; no quote is built without a real value.
	const depositCurrency = depositSendCurrency ?? "USD";

	// Keep the store in sync so the review page can rebuild FeeQuote on location change.
	useEffect(() => {
		rampsActions.setCashInSendCurrency(depositSendCurrency);
	}, [depositSendCurrency]);

	// Build QuoteRequest for deposit (stagingInfo + customFields from cashInLocation)
	const buildDepositQuoteRequest = useMemo((): QuoteRequest | null => {
		return buildDepositQuoteRequestFromParams({
			cashInLocation: store.cashInLocation,
			payoutCountry: store.payoutCountry,
			state: store.state,
			countries: store.countries,
			inputAmount,
			sendCurrencyCode: depositSendCurrency,
		});
	}, [
		store.cashInLocation,
		store.payoutCountry,
		store.state,
		store.countries,
		inputAmount,
		depositSendCurrency,
	]);

	// Helper to extract display data from quote
	const getQuoteDisplayData = (quoteResponse: QuoteResponse | undefined) => {
		if (!quoteResponse?.transactions?.[0]) return null;
		const transaction = quoteResponse.transactions[0];
		const sendAmountDetails = transaction.sendAmountDetails;
		const payoutAmountDetails = transaction.payoutAmountDetails;
		// Deposit flow: use sendAmountDetails.fxRate for exchange rate display (never payoutAmountDetails)
		const fxRate = Number(sendAmountDetails?.fxRate ?? 1);
		const feesValue =
			Number(payoutAmountDetails?.partnerFees?.value ?? 0) *
				Number(sendAmountDetails?.fxRate ?? 1) || 0;
		const totalValue =
			Number(sendAmountDetails?.totalAmount?.value ?? 0) ||
			Number(inputAmount || 0) + feesValue;
		// Cash-in taxes are charged at the location in local fiat, so prefer the
		// sendAmountDetails side; payout-side taxes are converted via fxRate.
		const taxes = resolveCashInTaxes({
			sendTaxes: sendAmountDetails?.taxes,
			payoutTaxes: payoutAmountDetails?.taxes,
			fxRate,
			depositCurrency,
		});
		// Exchange amount: USDC deposit amount converted to local currency
		const exchangeAmountValue = Number(inputAmount || 0) * fxRate;
		const exchangeAmount = {
			value: exchangeAmountValue,
			currencyCode:
				sendAmountDetails?.totalAmount?.currencyCode || depositCurrency,
		};
		return {
			exchangeRate: fxRate,
			exchangeAmount,
			fees: {
				value: feesValue,
				currencyCode: depositCurrency,
			},
			partnerFees: {
				value: feesValue,
				currencyCode: depositCurrency,
			},
			totalPayAmount: {
				value: totalValue,
				currencyCode: depositCurrency,
			},
			taxes,
		};
	};

	// Normalize amount for query key so "100" and "100.00" match the same cache entry.
	// Without this, setInputAmount(formatAmount(value)) changes the key mid-refetch,
	// causing the component to subscribe to an empty query.
	const amountForQuoteKey =
		inputAmount && !Number.isNaN(Number(inputAmount))
			? Number(inputAmount).toFixed(2)
			: "";

	const quoteQuery = useQuery({
		queryKey: [
			"depositQuote",
			"deposit",
			store.payoutCountry,
			store.state,
			store.assetCode,
			amountForQuoteKey,
			store.cashInLocation?.locationId,
			depositSendCurrency,
			// See ramps.calculator.tsx for rationale — refetch when active
			// locale changes so the `userLanguage` payload stays in sync with
			// what Lingui activated.
			store.lang,
		],
		queryFn: async (): Promise<QuoteResponse> => {
			const request = buildDepositQuoteRequest;
			if (!request) throw new Error("Missing quote request");
			return apiClient.quote.getQuote(request, {
				params: { operation: "deposit" },
			});
		},
		enabled: (() => {
			const effectiveCountry = store.payoutCountry;
			const serviceOptions = serviceOptionsQuery.data?.serviceOptions ?? [];
			const hasPayCurrency = serviceOptions.some(
				(option) => (option.payoutCurrencyCodes?.length ?? 0) > 0,
			);
			const isValidAmountForQuote = () => {
				if (!inputAmount) return false;
				const numAmount = Number.parseFloat(inputAmount);
				if (numAmount < minAmount || numAmount > maxAmount) return false;
				if (isAmountEditable) return isAmountReady(numAmount);
				return !Number.isNaN(numAmount);
			};
			return (
				!!authToken &&
				!!effectiveCountry &&
				!!store.cashInLocation &&
				hasPayCurrency &&
				isValidAmountForQuote() &&
				readyForQuote &&
				!!buildDepositQuoteRequest
			);
		})(),
		staleTime: 30000,
		retry: 1,
	});

	// Surface quote failures so they don't appear as a silent "no trigger" state.
	useEffect(() => {
		if (!quoteQuery.isError) return;
		const errorMessage =
			quoteQuery.error instanceof Error
				? quoteQuery.error.message
				: t`Error loading quote. Please try again.`;
		const sentryContext = {
			tags: { area: "deposit-quote" },
			extra: {
				destinationCountryCode: store.payoutCountry,
				cashInLocationId: store.cashInLocation?.locationId,
				inputAmount,
				isAmountEditable,
			},
		};
		if (quoteQuery.error instanceof Error) {
			Sentry.captureException(quoteQuery.error, sentryContext);
		} else {
			Sentry.captureMessage(errorMessage, { ...sentryContext, level: "error" });
		}
		toast.apiError(errorMessage);
	}, [quoteQuery.isError, quoteQuery.error]);

	// Sync quote result to store so rest of flow has quoteResponse
	// Also capture senderProfile when present (prefills user data for name, address, etc.)
	useEffect(() => {
		if (quoteQuery.data) {
			rampsActions.setQuoteResponse(quoteQuery.data);
			if (quoteQuery.data.senderProfile) {
				rampsActions.setSenderProfile(quoteQuery.data.senderProfile);
			}
		}
	}, [quoteQuery.data]);

	// Reset isPendingNewQuote when quote query finishes (success or error)
	// This ensures the continue button enables even if quote fails after retries
	useEffect(() => {
		if (
			!quoteQuery.isFetching &&
			(quoteQuery.isSuccess || quoteQuery.isError)
		) {
			setIsPendingNewQuote(false);
		}
	}, [quoteQuery.isFetching, quoteQuery.isSuccess, quoteQuery.isError]);

	// Fallback: Reset isPendingNewQuote after timeout to prevent button from being stuck
	// Quote is for display only and shouldn't block navigation
	useEffect(() => {
		if (!isPendingNewQuote) return;

		const timeoutId = setTimeout(() => {
			setIsPendingNewQuote(false);
		}, 3000); // Reset after 3 seconds max - quote shouldn't block flow

		return () => clearTimeout(timeoutId);
	}, [isPendingNewQuote]);

	// Auto-trigger quote for locked amounts
	// For deposit flow, we don't require state selection
	useEffect(() => {
		const hasLockedAmount =
			(store.apiVersion === "v3" && store.prepareAmount) ||
			(!isAmountEditable && search.amount);
		const amountToCheck =
			store.apiVersion === "v3" && store.prepareAmount
				? store.prepareAmount
				: search.amount || inputAmount;

		if (
			hasLockedAmount &&
			!!authToken &&
			!!store.payoutCountry &&
			Number(amountToCheck) >= 1
		) {
			setReadyForQuote(true);
		}
	}, [
		store.prepareAmount,
		store.apiVersion,
		isAmountEditable,
		authToken,
		search.amount,
		store.payoutCountry,
		inputAmount,
	]);

	// When user selects a location, trigger FeeQuote if amount is already populated and valid.
	// Only run when location actually changes (not on every inputAmount keystroke), to avoid
	// duplicate quote requests when amount is formatted (e.g. "15" -> "15.00").
	const currentLocationId = store.cashInLocation?.locationId;
	useEffect(() => {
		if (!store.cashInLocation) return;

		if (!inputAmount) {
			// Sync ref when amount is empty so we don't falsely trigger when user later types
			prevLocationIdRef.current = currentLocationId;
			return;
		}
		const numAmount = Number.parseFloat(inputAmount);
		if (
			Number.isNaN(numAmount) ||
			numAmount < minAmount ||
			numAmount > maxAmount
		)
			return;
		if (isAmountEditable && !isAmountReady(numAmount)) return;

		// Only trigger when location was newly selected (or changed), not when inputAmount changes
		const locationJustSelected =
			prevLocationIdRef.current !== currentLocationId;
		prevLocationIdRef.current = currentLocationId;
		if (!locationJustSelected) return;

		setIsPendingNewQuote(true);
		setReadyForQuote(true);
		// Explicit refetch: when location changes, we need a new quote for the new location
		quoteQuery.refetch();
	}, [
		store.cashInLocation,
		currentLocationId,
		inputAmount,
		minAmount,
		maxAmount,
		isAmountEditable,
		quoteQuery.refetch,
	]);

	const handleAmountChange = async (value: string) => {
		if (readyForQuote) {
			setReadyForQuote(false);
		}
		setInputAmount(value);
		setIsPendingNewQuote(true);

		if (readyForQuoteTimeoutRef.current) {
			clearTimeout(readyForQuoteTimeoutRef.current);
		}

		const numValue = Number(value);
		if (!value || numValue < 1) return;

		readyForQuoteTimeoutRef.current = setTimeout(() => {
			const formattedAmount = formatAmount(value);
			setInputAmount(formattedAmount);
			const error = minMaxError(formattedAmount, minAmount, maxAmount);
			if (error.isError) {
				toast.validationError(error.errorMsg || "Invalid amount");
				setIsPendingNewQuote(false);
				return;
			}

			// Check max send limit
			if (Number(formattedAmount) > maxSendLimit) {
				const errorMsg = t`Maximum send limit is ${maxSendLimit} USDC`;
				toast.validationError(errorMsg);
				setIsPendingNewQuote(false);
				return;
			}

			trackEvent(ANALYTICS_EVENTS.AMOUNT_CHANGED, {
				amount: Number(formattedAmount),
				source_currency: store.assetCode || "USDC",
				destination_currency: depositCurrency,
				flow_type: "deposit",
			} as Record<string, unknown>);

			setReadyForQuote(true);
			setIsPendingNewQuote(false);
			// Explicit refetch: React Query may not reliably fetch when enabled flips false->true
			// in a debounced timeout; refetch() ensures the quote is requested.
			quoteQuery.refetch();
		}, 1500);
	};

	const handleAmountCommit = () => {
		if (readyForQuote) {
			setReadyForQuote(false);
		}
		setIsPendingNewQuote(true);

		if (readyForQuoteTimeoutRef.current) {
			clearTimeout(readyForQuoteTimeoutRef.current);
		}

		if (!inputAmount || Number(inputAmount) < 1) return;

		setInputAmount(formatAmount(inputAmount));
		const error = minMaxError(inputAmount, minAmount, maxAmount);
		if (error.isError) {
			toast.validationError(error.errorMsg || "Invalid amount");
			setIsPendingNewQuote(false);
			return;
		}

		// Check max send limit
		if (Number(inputAmount) > maxSendLimit) {
			const errorMsg = t`Maximum send limit is ${maxSendLimit} USDC`;
			toast.validationError(errorMsg);
			setIsPendingNewQuote(false);
			return;
		}

		setReadyForQuote(true);
		setIsPendingNewQuote(false);
		// Explicit refetch to ensure quote is requested on commit (Enter/blur)
		quoteQuery.refetch();
	};

	// Event handlers for country/state
	const handleCountryChange = (newIso3: string) => {
		setReadyForQuote(false);

		// Clear cash-in location and quote when country changes
		if (store.cashInLocation && store.payoutCountry !== newIso3) {
			rampsActions.clearCashInLocation();
			rampsActions.clearQuoteResponse();
		}

		rampsActions.setPayoutCountry(newIso3);
		rampsActions.setCalculatorData({
			country: newIso3,
			state: "",
			amount: inputAmount || "0.00",
			currency: store.assetCode || "USDC",
		});

		setTimeout(() => {
			if (inputAmount && Number(inputAmount) >= 1) {
				setReadyForQuote(true);
			}
		}, 100);
	};

	// Handler for bottom sheet location selection
	// For deposit flow, only country selection is needed (no state/subdivision)
	const handleLocationSelect = (value: string) => {
		// Always handle as country selection for deposit flow
		handleCountryChange(value);
	};

	// RAMPS-4410: The SEP-9 name fields arrive inside senderProfile whether the
	// user is new or returning, so senderProfile's presence alone can't tell them
	// apart. A returning user with a saved MoneyGram profile carries a
	// primaryIdentification; a brand-new user does not. Only brand-new users are
	// asked to verify the (possibly partner-supplied) name before it is
	// permanently saved at sendValidate.
	const senderProfile = store.quoteResponse?.senderProfile;
	const isBrandNewUser =
		!!senderProfile && !senderProfile.primaryIdentification?.id;

	// Full legal name assembled from the store for the confirmation sheet.
	const getConfirmationName = () =>
		[
			capitalizeFirstLetter(store.firstName),
			capitalizeFirstLetter(store.middleName),
			capitalizeFirstLetter(store.lastName),
			capitalizeFirstLetter(store.secondLastName),
		]
			.filter(Boolean)
			.join(" ");

	// Advance through the normal deposit flow (phone → … → review).
	const proceedToNextPage = () => {
		continueToNextPage(search);
	};

	// User confirmed their name is correct: continue the flow.
	const handleConfirmName = () => {
		setShowNameConfirmDrawer(false);
		proceedToNextPage();
	};

	// User needs to fix their name: send them to the name page to edit it.
	const handleEditName = () => {
		setShowNameConfirmDrawer(false);
		navigate(
			navigateWithRampsState(search, {
				to: "/ramps/name",
				search: { step: "name" },
			}),
		);
	};

	const handleContinue = () => {
		// Validate amount
		const amountValidation = validateAmount(inputAmount);
		if (!amountValidation.isValid) {
			const errorMsg = amountValidation.error || t`Invalid amount`;
			toast.validationError(errorMsg);
			return;
		}

		// Check max send limit
		if (Number(inputAmount) > maxSendLimit) {
			const errorMsg = t`Maximum send limit is ${maxSendLimit} USDC`;
			toast.validationError(errorMsg);
			return;
		}

		// Store the amount and set service option for deposit flow
		rampsActions.setCalculatorData({
			country: store.payoutCountry,
			state: getCountriesWithSubdivisions().includes(store.payoutCountry)
				? store.state || store.cashInLocation?.state || ""
				: "",
			amount: inputAmount || "0.00",
			currency: store.assetCode || "USDC",
		});
		rampsActions.setServiceOption("DIRECT_TO_ACCT");

		// Track continue click
		if (experimentVariant !== null) {
			trackEvent(ANALYTICS_EVENTS.CALCULATOR_CONTINUE_CLICKED, {
				...getExperimentProperties(),
				flow_type: "deposit",
				from_review: search.from_review === "true",
			} as Record<string, unknown>);
		}

		// Track flow_started for funnel analysis (always fires, regardless of experiment)
		trackEvent("flow_started", {
			flow_type: "deposit",
			amount: inputAmount ? Number(inputAmount) : undefined,
			country: store.payoutCountry,
			from_review: search.from_review === "true",
		} as Record<string, unknown>);

		// If coming from review (editing amount), call quote API before returning to review
		if (search.from_review === "true") {
			if (!store.cashInLocation) {
				toast.error(t`Missing deposit location. Please select a location.`);
				return;
			}
			const locationCountryIso3 =
				convertISO2ToISO3(store.cashInLocation.country, store.countries) ||
				store.cashInLocation.country;
			if (locationCountryIso3 !== store.payoutCountry) {
				toast.error(
					t`Country changed. Please select a new deposit location for the selected country.`,
				);
				return;
			}

			// Send currency comes from serviceOptions (DIRECT_TO_ACCT sendCurrencyCodes[0])
			const localFiatCurrency = depositSendCurrency;
			if (!localFiatCurrency) {
				toast.error(t`Error loading quote. Please try again.`);
				return;
			}

			// Get the state from store (set when location was selected)
			// For countries that support state/subdivision, this comes from the selected location
			const locationState = store.state;

			const destinationCountrySubdivisionCode =
				buildDestinationCountrySubdivisionCode({
					payoutCountry: store.payoutCountry,
					countries: store.countries,
					locationState,
				});

			// Build customFields with selectedLocation (reconstruct PickupLocationRaw structure from CashInLocation)
			const customFields = store.cashInLocation
				? [
						{
							key: "selectedLocation",
							value: JSON.stringify({
								locationDetails: {
									name: store.cashInLocation.name,
									locationId: store.cashInLocation.locationId,
									kioskId: "", // Not available in CashInLocation
									addressLine1:
										store.cashInLocation.address.split(", ")[0] ||
										store.cashInLocation.address,
									addressLine2:
										store.cashInLocation.address.split(", ")[1] || "",
									addressLine3:
										store.cashInLocation.address.split(", ")[2] || "",
									city: store.cashInLocation.city,
									state: store.cashInLocation.state,
									country: store.cashInLocation.country, // Should be ISO Alpha-2 (e.g., "US")
									postalCode: "", // Not available in CashInLocation
									retailerId: "", // Not available in CashInLocation
									pwc: "0", // Not available in CashInLocation
									distance: 0, // Not available in CashInLocation
									distanceuom: "mile", // Default
									latitude: Number.parseFloat(store.cashInLocation.latitude),
									longitude: Number.parseFloat(store.cashInLocation.longitude),
									bankAccountRequired: false, // Not available in CashInLocation
								},
								locationOperatingHours: {
									monHours: "",
									tueHours: "",
									wedHours: "",
									thuHours: "",
									friHours: "",
									satHours: "",
									sunHours: "",
								},
								locationPhone: {
									addToPhone: false,
									phone: "",
								},
								locationServices: {
									amazonCard: false,
									sendMoney: false,
									receiveMoney: true,
									billPayment: false,
									reloadOtherCard: false,
									addToPhone: false,
									payPal: false,
									moneyOrder: false,
									recCurrencies: "",
									sendCurrencies: "",
									xpressPackage: false,
									purchaseMoneygramCard: false,
									reloadMoneygramCard: false,
									receiveToCard: false,
									expressPayment: false,
									payWithCash: false,
									formFreeCompletion: store.cashInLocation.formFreeCompletion,
									directToAccount: false,
								},
								locationAnalytics: {
									cashInOverallScore: 0,
									cashOutOverallScore: 0,
									feedback: { thumbsUp: 0, thumbsDown: 0 },
									formFreeCompletion: {},
									directToAccount: {},
									receiveMoney: {},
								},
							}),
						},
					]
				: undefined;

			// Build quote request with updated amount and existing location
			const quoteRequest: QuoteRequest = {
				targetAudience: "CONSUMER_FACING",
				userLanguage: getActiveUserLanguageTag(),
				destinationCountryCode: store.payoutCountry || "USA",
				destinationCountrySubdivisionCode,
				payoutAmount: {
					value: Number(inputAmount) || 0,
					currencyCode: "USDC",
				},
				payoutOptionCode: "DIRECT_TO_ACCT",
				sendCurrencyCode: localFiatCurrency,
				sendAmount: {
					value: Number(inputAmount) || 0,
					currencyCode: localFiatCurrency,
				},
				payoutCurrencyCode: "USDC",
				stagingInfo: {
					stageTransaction: true,
					sendLocationAgentPartnerId: store.cashInLocation.locationId,
					formFreeCompletion: store.cashInLocation.formFreeCompletion,
				},
				customFields,
			};

			trackEvent("deposit_amount_edit_quote_requested", {
				amount: inputAmount,
				country: store.payoutCountry,
				location_id: store.cashInLocation.locationId,
			} as Record<string, unknown>);

			// Call quote API - navigation happens in onSuccess
			quoteMutation.mutate(quoteRequest);
			return;
		}

		// RAMPS-4410: before advancing a brand-new user toward sendValidate, have
		// them verify the (possibly partner-prefilled) name is correct. Returning
		// users skip this — their name is already locked to their MoneyGram profile.
		if (isBrandNewUser) {
			setShowNameConfirmDrawer(true);
			return;
		}

		// Navigate to next step (phone) in deposit flow
		proceedToNextPage();
	};

	// Get display text for current location selection
	// For deposit flow, only show country (no state/region)
	const getLocationDisplayText = (): string => {
		if (countriesLoading) {
			return t`Loading...`;
		}
		return selectedCountry?.label || t`Select country`;
	};

	// Get quote display data
	// Has valid quote for Continue: from query or store fallback (when returning from later UI screens)
	const hasValidQuoteForContinue = (() => {
		if (quoteQuery.data) return true;
		if (!store.quoteResponse?.transactions?.[0] || isPendingNewQuote)
			return false;
		const tx = store.quoteResponse.transactions[0];
		const quoteAmount = tx.payoutAmountDetails?.totalAmount?.value;
		const currentAmount = Number(inputAmount);
		if (quoteAmount == null || Number.isNaN(currentAmount)) return false;
		const amountMatches = Math.abs(quoteAmount - currentAmount) < 0.01;
		return amountMatches && !!store.cashInLocation;
	})();
	const effectiveQuoteResponse =
		quoteQuery.data ??
		(hasValidQuoteForContinue ? store.quoteResponse : undefined);
	const quoteDisplayData = getQuoteDisplayData(effectiveQuoteResponse);
	const depositQuoteData = quoteDisplayData
		? {
				exchangeRate: quoteDisplayData.exchangeRate,
				fees: quoteDisplayData.partnerFees,
				totalPayAmount: quoteDisplayData.totalPayAmount,
				exchangeAmount: quoteDisplayData.exchangeAmount,
			}
		: null;

	const isLoadingFxOrQuote = quoteQuery.isLoading;

	if (isCashInMaintenance) {
		return <MaintenancePage flow="cash-in" />;
	}

	if (coreApiErrorCodes.length > 0) {
		return (
			<CoreApiErrorPage
				flow="cash-in"
				errorCodes={coreApiErrorCodes}
				transactionId={store.transactionId}
			/>
		);
	}

	return (
		<div
			className={`flex flex-col overflow-x-hidden ${
				!store.cashInLocation ? "h-dvh overflow-hidden" : "min-h-dvh"
			}`}
		>
			{/* Country selector and progress - padded */}
			<div className="shrink-0 px-4 bg-ramps-background-base">
				<div className="flex shrink-0 items-center justify-center py-6">
					{/* Country/Location selector */}
					<div className="mx-4">
						{countriesLoading ? (
							<div className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-ramps-background-surface-2">
								<div className="w-5 h-5 rounded-full bg-gray-300 animate-pulse" />
								<RampsText
									variant="body-default"
									className="font-medium"
									color="primary"
								>
									{t`Loading...`}
								</RampsText>
							</div>
						) : (
							<RampsSelectBottomSheet
								label=""
								sheetTitle={t`Select country`}
								options={countryOptions.map((c) => ({
									value: c.value,
									label: c.label,
									countryCode: c.countryCode,
								}))}
								value={selectedCountryIso3}
								onSelect={handleLocationSelect}
								size="full"
								searchPlaceholder={t`Search countries...`}
								renderOptionIcon={(option) =>
									option.countryCode ? (
										<CircleFlag
											countryCode={option.countryCode}
											className="w-6 h-6"
										/>
									) : null
								}
								renderTriggerContent={() => (
									<div className="flex items-center gap-2">
										<CircleFlag
											countryCode={selectedCountry?.countryCode || "us"}
											className="w-5 h-5"
										/>
										<RampsText
											variant="body-default"
											className="font-medium"
											color="primary"
										>
											{getLocationDisplayText()}
										</RampsText>
									</div>
								)}
								triggerClassName="bg-ramps-background-surface-2 rounded-lg px-4 py-2 w-auto"
								placeholder={t`Select country`}
							/>
						)}
					</div>
				</div>

				{/* Progress bar */}
				<div className="mb-4 shrink-0">
					<Progress
						value={10}
						className="h-1 bg-ramps-background-surface-2 *:bg-ramps-text-primary"
						data-testid="deposit-progress"
					/>
				</div>
			</div>

			{/* No location: show Locator (full width). Location selected: show calculator + location row (padded) */}
			{!store.cashInLocation ? (
				<div className="flex min-h-0 flex-1 flex-col overflow-hidden">
					<Locator
						onContinue={() => {
							/* Location already stored by Locator; re-render shows calculator */
						}}
					/>
				</div>
			) : (
				<div className="flex flex-1 flex-col min-h-0 px-4 bg-ramps-background-base">
					{/* Location "Select" row - opens Locator in bottom sheet */}
					<div className="mb-4">
						<button
							type="button"
							onClick={() => setLocationSheetOpen(true)}
							className="w-full text-left bg-ramps-background-surface-2 border border-ramps-background-surface-2 rounded-lg h-14 px-4 py-2 flex items-center gap-3"
						>
							<div className="flex-1 min-w-0 flex flex-col justify-center">
								<RampsText
									variant="body-caption"
									color="secondary"
									className="mb-0.5"
								>
									{t`Location`}
								</RampsText>
								<RampsText
									variant="body-default"
									color="primary"
									className="truncate"
								>
									{store.cashInLocation.name} - {store.cashInLocation.address}
								</RampsText>
							</div>
							<ChevronDown className="w-6 h-6 text-ramps-text-secondary shrink-0" />
						</button>
					</div>

					{/* Deposit calculator section - A/B test with feature flag */}
					{isNewCalculatorEnabled ? (
						<div className="mb-16">
							<DepositCalculator
								inputAmount={inputAmount}
								isAmountEditable={isAmountEditable}
								onAmountChange={handleAmountChange}
								onAmountCommit={handleAmountCommit}
								assetCode={store.assetCode || "USDC"}
								payCurrency={depositCurrency}
								countryCode={selectedCountry?.countryCode || "us"}
								quoteData={depositQuoteData}
								isLoading={isLoadingFxOrQuote}
							/>
						</div>
					) : (
						<div className="text-center mb-16">
							<RampsText variant="body-bold" color="secondary" className="mb-2">
								{t`Deposit amount`}
							</RampsText>

							<div className="flex items-center justify-center gap-3 mb-6">
								<EditableText
									value={inputAmount}
									isEditable={isAmountEditable}
									onChange={handleAmountChange}
									onCommit={handleAmountCommit}
									className="text-6xl font-normal"
									variant="number-md"
									color="primary"
									placeholder="0.00"
									caretWidthPx={4}
									blinkMs={900}
									autoFocus={isAmountEditable}
								/>
								<div className="flex flex-col items-center gap-0">
									<TokenUSDC className="w-6 h-6" />
									<RampsText variant="body-bold" color="disabled">
										{store.assetCode}
									</RampsText>
								</div>
							</div>

							<div className="mb-6">
								<RampsText
									variant="body-bold"
									color="secondary"
									className="mb-2"
								>
									{t`Exchange amount`}
								</RampsText>
								<div className="flex items-center justify-center gap-3">
									<RampsText
										variant="number-md"
										color="primary"
										className="text-6xl font-normal"
									>
										{quoteDisplayData &&
										inputAmount &&
										Number.isFinite(quoteDisplayData.exchangeAmount?.value)
											? formatNumber(quoteDisplayData.exchangeAmount.value)
											: "0.00"}
									</RampsText>
									<div className="flex flex-col items-center gap-0.5">
										<CircleFlag
											countryCode={selectedCountry?.countryCode || "us"}
											className="w-4 h-4"
										/>
										<RampsText variant="body-bold" color="disabled">
											{quoteDisplayData?.exchangeAmount?.currencyCode ??
												depositCurrency}
										</RampsText>
									</div>
								</div>
							</div>

							{quoteDisplayData && inputAmount && isNewCalculatorEnabled ? (
								<RampsText variant="body-default" color="primary">
									{depositCurrency === "USD"
										? t`Exchange rate: 1 USD = 1 USDC`
										: `${t`Exchange rate:`} 1 USD = 1 ${store.assetCode || "USDC"} = ${formatNumber(quoteDisplayData.exchangeRate, { minimumFractionDigits: 2, maximumFractionDigits: 4 })} ${depositCurrency}`}
								</RampsText>
							) : isLoadingFxOrQuote ? (
								<RampsText variant="body-default" color="primary">
									{t`Loading exchange rate...`}
								</RampsText>
							) : null}
							<div className="h-6" />
						</div>
					)}
				</div>
			)}

			{/* Fees section + Continue - only when location selected */}
			{store.cashInLocation && (
				<div className="rounded-t-2xl p-6 mt-auto bg-gradient-ramps-status-bl">
					{!isNewCalculatorEnabled && (
						<div className="flex justify-between items-center mb-3">
							<RampsText
								variant="body-default"
								color="primary"
								style={{ fontSize: 14, lineHeight: "22px" }}
							>
								{t`Exchange rate`}
							</RampsText>
							{isLoadingFxOrQuote ? (
								<Skeleton
									className="h-5 w-32 bg-gray-200/30 border border-gray-300/20"
									style={{ fontSize: 14, lineHeight: "22px" }}
								/>
							) : quoteDisplayData && inputAmount ? (
								<RampsText
									variant="body-default"
									color="primary"
									style={{ fontSize: 14, lineHeight: "22px" }}
								>
									{depositCurrency === "USD"
										? "1 USD = 1 USDC"
										: `1 USD = 1 ${store.assetCode || "USDC"} = ${formatNumber(quoteDisplayData.exchangeRate, { minimumFractionDigits: 2, maximumFractionDigits: 4 })} ${depositCurrency}`}
								</RampsText>
							) : (
								<RampsText
									variant="body-default"
									color="secondary"
									style={{ fontSize: 14, lineHeight: "22px" }}
								>
									--
								</RampsText>
							)}
						</div>
					)}
					<div className="flex justify-between items-center mb-3">
						<RampsText
							variant="body-default"
							color="primary"
							style={{ fontSize: 14, lineHeight: "22px" }}
						>
							{t`Fees`}
						</RampsText>
						{isLoadingFxOrQuote ? (
							<Skeleton
								className="h-5 w-20 bg-gray-200/30 border border-gray-300/20"
								style={{ fontSize: 14, lineHeight: "22px" }}
							/>
						) : (
							<RampsText
								variant="body-default"
								color="primary"
								style={{ fontSize: 14, lineHeight: "22px" }}
							>
								{quoteDisplayData && inputAmount
									? `${formatNumber(quoteDisplayData.fees.value)} ${depositCurrency}`
									: `0 ${depositCurrency}`}
							</RampsText>
						)}
					</div>
					{quoteDisplayData?.taxes && quoteDisplayData.taxes.value > 0 && (
						<div className="flex justify-between items-center mb-3">
							<RampsText
								variant="body-default"
								color="primary"
								style={{ fontSize: 14, lineHeight: "22px" }}
							>
								{t`Taxes`}
							</RampsText>
							<RampsText
								variant="body-default"
								color="primary"
								style={{ fontSize: 14, lineHeight: "22px" }}
							>
								{formatNumber(quoteDisplayData.taxes.value)}{" "}
								{quoteDisplayData.taxes.currencyCode}
							</RampsText>
						</div>
					)}
					{/* Pay at location (Total) row with info icon - opens disclaimer sheet */}
					<div className="flex justify-between items-center mb-6">
						<button
							type="button"
							onClick={() => setDisclaimerSheetOpen(true)}
							className="flex items-center gap-2 text-left group"
						>
							<Info className="w-4 h-4 text-ramps-text-secondary shrink-0" />
							<RampsText
								variant="body-bold"
								color="primary"
								className="underline decoration-solid"
							>
								{t`Pay at location`}
							</RampsText>
						</button>
						{isLoadingFxOrQuote ? (
							<Skeleton className="h-6 w-24 bg-gray-200/30 border border-gray-300/20" />
						) : (
							<RampsText variant="body-bold" color="primary">
								{quoteDisplayData && inputAmount
									? `${formatNumber(quoteDisplayData.totalPayAmount.value)} ${depositCurrency}`
									: `0 ${depositCurrency}`}
							</RampsText>
						)}
					</div>

					<RampsButton
						onClick={handleContinue}
						disabled={
							!store.payoutCountry ||
							!store.cashInLocation ||
							!inputAmount ||
							isPendingNewQuote ||
							quoteMutation.isPending ||
							!hasValidQuoteForContinue ||
							minMaxError(inputAmount, minAmount, maxAmount).isError ||
							Number(inputAmount) > maxSendLimit
						}
						loading={quoteMutation.isPending}
						className="w-full h-12 rounded-lg font-medium bg-black text-white hover:bg-gray-800"
					>
						{t`Continue`}
					</RampsButton>
				</div>
			)}

			{/* Location selector bottom sheet */}
			<Drawer open={locationSheetOpen} onOpenChange={setLocationSheetOpen}>
				<DrawerContent className="flex h-[85vh] max-h-[90vh] flex-col">
					<DrawerTitle className="sr-only">{t`Pick a location`}</DrawerTitle>
					<div className="flex min-h-0 flex-1 flex-col overflow-hidden">
						<Locator
							onContinue={() => setLocationSheetOpen(false)}
							title={t`Pick a location`}
							className="min-h-0"
						/>
					</div>
				</DrawerContent>
			</Drawer>

			{/* Legal disclaimer bottom sheet for Pay at location */}
			<Drawer open={disclaimerSheetOpen} onOpenChange={setDisclaimerSheetOpen}>
				<DrawerContent showGrabBar={false}>
					<DrawerTitle className="sr-only">{t`Exchange amount and fees`}</DrawerTitle>
					{/* Close row stays pinned; the copy below scrolls so it is never
					    clipped off the bottom on short viewports or in translations
					    that wrap to more lines than English (RAMPS-4427). */}
					<div className="flex shrink-0 flex-col px-6 pt-6">
						<div className="mb-4 flex w-full items-center justify-end">
							<DrawerClose asChild>
								<button
									type="button"
									className="flex size-6 items-center justify-center rounded-full hover:bg-neutral-100 transition-colors"
									aria-label={t`Close`}
								>
									<X className="w-6 h-6 text-ramps-text-primary" />
								</button>
							</DrawerClose>
						</div>
					</div>
					<DrawerScrollBody className="px-6 pb-10">
						<div className="flex flex-col gap-6">
							<div className="flex flex-col gap-2">
								<RampsText
									variant="body-bold"
									color="primary"
									style={{ fontSize: 18, lineHeight: "20px" }}
								>
									{t`Exchange amount`}
								</RampsText>
								<RampsText
									variant="body-caption"
									color="secondary"
									className="whitespace-pre-wrap"
								>
									{t`The exchange amount is an estimate and may change at the time of payment, affecting your total pay amount.`}
								</RampsText>
							</div>
							<div className="flex flex-col gap-2">
								<RampsText
									variant="body-bold"
									color="primary"
									style={{ fontSize: 18, lineHeight: "20px" }}
								>
									{t`Fees`}
								</RampsText>
								<RampsText
									variant="body-caption"
									color="secondary"
									className="whitespace-pre-wrap"
								>
									{t`Fees are added to your Pay at location in local currency.`}
								</RampsText>
							</div>
						</div>
					</DrawerScrollBody>
				</DrawerContent>
			</Drawer>

			{/* Legal name verification for brand-new users (RAMPS-4410) */}
			<LegalNameConfirmationDrawer
				open={showNameConfirmDrawer}
				onOpenChange={setShowNameConfirmDrawer}
				name={getConfirmationName()}
				onConfirm={handleConfirmName}
				onEdit={handleEditName}
			/>
		</div>
	);
}

export const Route = createFileRoute("/ramps/deposit")({
	validateSearch: (search) => validateRampsSearch(search),
	component: DepositPage,
});
